import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ConfirmDialogComponent,
  ConfirmDialogInput,
} from '@qtek/libs/dialog-routes';
import { AiChatStore } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AiChat, Message } from '@qtek/shared/models';
import { filter, take } from 'rxjs';

/**
 * SD-2661 — the Allo chat-history list: a "Pinned" section above a "Recents" section, with
 * per-row rename / pin / delete.
 *
 * Extracted so the two panels that show history — the inline right-sidenav and the popped-out
 * {@code qt-ai-chat-popout} window — share one implementation. Before this they each carried a
 * full copy of the list markup and its editing state, and the copies had already begun to drift.
 *
 * The component owns everything list-related (sections, paging, rename, pin, delete) and reports
 * only what the host needs to react to: which chat was picked, and which was deleted.
 */
@Component({
  selector: 'qt-ai-chat-history',
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TranslationCoreModule,
  ],
  templateUrl: './ai-chat-history.component.html',
  styleUrls: ['./ai-chat-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatHistoryComponent {
  /** Highlights the row for the chat currently open in the host panel. */
  readonly selectedChatId = input<string | null>(null);

  /** A row was clicked — the host loads that chat. */
  readonly chatSelected = output<string>();

  /**
   * A chat was deleted. The host decides what to do about it; the only case that matters is the
   * user deleting the thread they are currently reading.
   */
  readonly chatDeleted = output<string>();

  private static readonly PAGE_SIZE = 10;

  private readonly aiChatStore = inject(AiChatStore);
  private readonly dialog = inject(MatDialog);

  readonly pinnedChats = this.aiChatStore.pinnedChats;
  readonly recentChats = this.aiChatStore.recentChats;
  readonly visibleCount = signal(AiChatHistoryComponent.PAGE_SIZE);

  readonly editingChatId = signal<string | null>(null);
  readonly editingTitle = signal('');

  /** Paging applies to Recents only, so a pinned thread can never be paged out of view. */
  readonly visibleRecents = computed(() =>
    this.recentChats().slice(0, this.visibleCount())
  );
  readonly hasMore = computed(
    () => this.recentChats().length > this.visibleCount()
  );
  readonly isEmpty = computed(
    () => this.pinnedChats().length === 0 && this.recentChats().length === 0
  );

  /** Reset paging — hosts call this when they (re)open the dropdown. */
  resetPaging(): void {
    this.visibleCount.set(AiChatHistoryComponent.PAGE_SIZE);
  }

  showMore(): void {
    const next = this.visibleCount() + AiChatHistoryComponent.PAGE_SIZE;
    this.visibleCount.set(Math.min(next, this.recentChats().length));
  }

  /**
   * A chat is pinned when it carries a `pinnedAt`. Lives here rather than inline in the template
   * so the null-and-undefined check stays in one place (the template lint bans `!=`).
   */
  isPinned(item: AiChat): boolean {
    return item?.pinnedAt != null;
  }

  select(chatId: string): void {
    this.chatSelected.emit(chatId);
  }

  // --- rename -------------------------------------------------------------

  startEditTitle(item: AiChat): void {
    this.editingChatId.set(item.id);
    this.editingTitle.set((item.title ?? '').trim());
  }

  onEditingTitleInput(event: Event): void {
    this.editingTitle.set((event.target as HTMLInputElement)?.value ?? '');
  }

  saveEditTitle(): void {
    const id = this.editingChatId();
    const title = this.editingTitle().trim();
    this.editingChatId.set(null);
    this.editingTitle.set('');
    if (!id) return;
    this.aiChatStore.updateChat({ id, res: { title: title || undefined } });
  }

  cancelEditTitle(): void {
    this.editingChatId.set(null);
    this.editingTitle.set('');
  }

  // --- pin / delete -------------------------------------------------------

  /**
   * Pin or unpin. Rides the existing Update Chat path — the BE takes a `pinned` boolean and
   * stamps/clears `pinnedAt` itself. A null `pinnedAt` on the wire would be dropped by MapStruct's
   * IGNORE null-value strategy, which is why unpin is a `false` rather than a cleared timestamp.
   */
  togglePin(item: AiChat): void {
    if (!item?.id) return;
    this.aiChatStore.updateChat({
      id: item.id,
      res: { pinned: !this.isPinned(item) },
    });
  }

  /**
   * Confirm, then delete. The BE soft-deletes, but this reads as permanent to the user, so it
   * goes behind the shared destructive confirm dialog.
   */
  confirmDelete(item: AiChat): void {
    if (!item?.id) return;

    const data: ConfirmDialogInput = {
      title: 'GNR_DEL_CHAT_TTL',
      message: new Message('GNR_DEL_CHAT_MSG'),
      acceptButton: 'CMD_DELETE',
      cancelButton: 'GNR_CANCEL',
      highlightTitle: true,
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, autoFocus: false })
      .afterClosed()
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        // Tell the host first. `deleteChat` drops the chat from the store immediately, including
        // the record of it as the chat a panel created for itself — which is what the host reads
        // to decide whether the deleted thread is the one it currently has open.
        this.chatDeleted.emit(item.id);
        this.aiChatStore.deleteChat({ id: item.id });
      });
  }
}
